/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const SWAP_EXACT_IN_DISCRIMINATOR = new Uint8Array([
  104, 104, 131, 86, 161, 189, 180, 216,
]);

export function getSwapExactInDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SWAP_EXACT_IN_DISCRIMINATOR,
  );
}

export type SwapExactInInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountVaultIn extends string | AccountMeta<string> = string,
  TAccountVaultOut extends string | AccountMeta<string> = string,
  TAccountToken0Mint extends string | AccountMeta<string> = string,
  TAccountToken1Mint extends string | AccountMeta<string> = string,
  TAccountUserIn extends string | AccountMeta<string> = string,
  TAccountUserOut extends string | AccountMeta<string> = string,
  TAccountTrader extends string | AccountMeta<string> = string,
  TAccountToken0Program extends string | AccountMeta<string> = string,
  TAccountToken1Program extends string | AccountMeta<string> = string,
  TAccountOracle extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountAuthority extends string
        ? ReadonlyAccount<TAccountAuthority>
        : TAccountAuthority,
      TAccountVaultIn extends string
        ? WritableAccount<TAccountVaultIn>
        : TAccountVaultIn,
      TAccountVaultOut extends string
        ? WritableAccount<TAccountVaultOut>
        : TAccountVaultOut,
      TAccountToken0Mint extends string
        ? ReadonlyAccount<TAccountToken0Mint>
        : TAccountToken0Mint,
      TAccountToken1Mint extends string
        ? ReadonlyAccount<TAccountToken1Mint>
        : TAccountToken1Mint,
      TAccountUserIn extends string
        ? WritableAccount<TAccountUserIn>
        : TAccountUserIn,
      TAccountUserOut extends string
        ? WritableAccount<TAccountUserOut>
        : TAccountUserOut,
      TAccountTrader extends string
        ? ReadonlySignerAccount<TAccountTrader> &
            AccountSignerMeta<TAccountTrader>
        : TAccountTrader,
      TAccountToken0Program extends string
        ? ReadonlyAccount<TAccountToken0Program>
        : TAccountToken0Program,
      TAccountToken1Program extends string
        ? ReadonlyAccount<TAccountToken1Program>
        : TAccountToken1Program,
      TAccountOracle extends string
        ? WritableAccount<TAccountOracle>
        : TAccountOracle,
      ...TRemainingAccounts,
    ]
  >;

export type SwapExactInInstructionData = {
  discriminator: ReadonlyUint8Array;
  amountIn: bigint;
  minAmountOut: bigint;
  tradeDirection: number;
  updateOracle: boolean;
};

export type SwapExactInInstructionDataArgs = {
  amountIn: number | bigint;
  minAmountOut: number | bigint;
  tradeDirection: number;
  updateOracle: boolean;
};

export function getSwapExactInInstructionDataEncoder(): FixedSizeEncoder<SwapExactInInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['amountIn', getU64Encoder()],
      ['minAmountOut', getU64Encoder()],
      ['tradeDirection', getU8Encoder()],
      ['updateOracle', getBooleanEncoder()],
    ]),
    (value) => ({ ...value, discriminator: SWAP_EXACT_IN_DISCRIMINATOR }),
  );
}

export function getSwapExactInInstructionDataDecoder(): FixedSizeDecoder<SwapExactInInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['amountIn', getU64Decoder()],
    ['minAmountOut', getU64Decoder()],
    ['tradeDirection', getU8Decoder()],
    ['updateOracle', getBooleanDecoder()],
  ]);
}

export function getSwapExactInInstructionDataCodec(): FixedSizeCodec<
  SwapExactInInstructionDataArgs,
  SwapExactInInstructionData
> {
  return combineCodec(
    getSwapExactInInstructionDataEncoder(),
    getSwapExactInInstructionDataDecoder(),
  );
}

export type SwapExactInAsyncInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountAuthority extends string = string,
  TAccountVaultIn extends string = string,
  TAccountVaultOut extends string = string,
  TAccountToken0Mint extends string = string,
  TAccountToken1Mint extends string = string,
  TAccountUserIn extends string = string,
  TAccountUserOut extends string = string,
  TAccountTrader extends string = string,
  TAccountToken0Program extends string = string,
  TAccountToken1Program extends string = string,
  TAccountOracle extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  authority?: Address<TAccountAuthority>;
  vaultIn: Address<TAccountVaultIn>;
  vaultOut: Address<TAccountVaultOut>;
  token0Mint: Address<TAccountToken0Mint>;
  token1Mint: Address<TAccountToken1Mint>;
  userIn: Address<TAccountUserIn>;
  userOut: Address<TAccountUserOut>;
  trader: TransactionSigner<TAccountTrader>;
  token0Program: Address<TAccountToken0Program>;
  token1Program: Address<TAccountToken1Program>;
  oracle?: Address<TAccountOracle>;
  amountIn: SwapExactInInstructionDataArgs['amountIn'];
  minAmountOut: SwapExactInInstructionDataArgs['minAmountOut'];
  tradeDirection: SwapExactInInstructionDataArgs['tradeDirection'];
  updateOracle: SwapExactInInstructionDataArgs['updateOracle'];
};

export async function getSwapExactInInstructionAsync<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountAuthority extends string,
  TAccountVaultIn extends string,
  TAccountVaultOut extends string,
  TAccountToken0Mint extends string,
  TAccountToken1Mint extends string,
  TAccountUserIn extends string,
  TAccountUserOut extends string,
  TAccountTrader extends string,
  TAccountToken0Program extends string,
  TAccountToken1Program extends string,
  TAccountOracle extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: SwapExactInAsyncInput<
    TAccountConfig,
    TAccountPool,
    TAccountAuthority,
    TAccountVaultIn,
    TAccountVaultOut,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUserIn,
    TAccountUserOut,
    TAccountTrader,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  SwapExactInInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountAuthority,
    TAccountVaultIn,
    TAccountVaultOut,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUserIn,
    TAccountUserOut,
    TAccountTrader,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    vaultIn: { value: input.vaultIn ?? null, isWritable: true },
    vaultOut: { value: input.vaultOut ?? null, isWritable: true },
    token0Mint: { value: input.token0Mint ?? null, isWritable: false },
    token1Mint: { value: input.token1Mint ?? null, isWritable: false },
    userIn: { value: input.userIn ?? null, isWritable: true },
    userOut: { value: input.userOut ?? null, isWritable: true },
    trader: { value: input.trader ?? null, isWritable: false },
    token0Program: { value: input.token0Program ?? null, isWritable: false },
    token1Program: { value: input.token1Program ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.authority.value) {
    accounts.authority.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([97, 117, 116, 104, 111, 114, 105, 116, 121]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount('pool', accounts.pool.value),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('authority', accounts.authority),
      getAccountMeta('vaultIn', accounts.vaultIn),
      getAccountMeta('vaultOut', accounts.vaultOut),
      getAccountMeta('token0Mint', accounts.token0Mint),
      getAccountMeta('token1Mint', accounts.token1Mint),
      getAccountMeta('userIn', accounts.userIn),
      getAccountMeta('userOut', accounts.userOut),
      getAccountMeta('trader', accounts.trader),
      getAccountMeta('token0Program', accounts.token0Program),
      getAccountMeta('token1Program', accounts.token1Program),
      getAccountMeta('oracle', accounts.oracle),
    ],
    data: getSwapExactInInstructionDataEncoder().encode(
      args as SwapExactInInstructionDataArgs,
    ),
    programAddress,
  } as SwapExactInInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountAuthority,
    TAccountVaultIn,
    TAccountVaultOut,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUserIn,
    TAccountUserOut,
    TAccountTrader,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >);
}

export type SwapExactInInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountAuthority extends string = string,
  TAccountVaultIn extends string = string,
  TAccountVaultOut extends string = string,
  TAccountToken0Mint extends string = string,
  TAccountToken1Mint extends string = string,
  TAccountUserIn extends string = string,
  TAccountUserOut extends string = string,
  TAccountTrader extends string = string,
  TAccountToken0Program extends string = string,
  TAccountToken1Program extends string = string,
  TAccountOracle extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  authority: Address<TAccountAuthority>;
  vaultIn: Address<TAccountVaultIn>;
  vaultOut: Address<TAccountVaultOut>;
  token0Mint: Address<TAccountToken0Mint>;
  token1Mint: Address<TAccountToken1Mint>;
  userIn: Address<TAccountUserIn>;
  userOut: Address<TAccountUserOut>;
  trader: TransactionSigner<TAccountTrader>;
  token0Program: Address<TAccountToken0Program>;
  token1Program: Address<TAccountToken1Program>;
  oracle?: Address<TAccountOracle>;
  amountIn: SwapExactInInstructionDataArgs['amountIn'];
  minAmountOut: SwapExactInInstructionDataArgs['minAmountOut'];
  tradeDirection: SwapExactInInstructionDataArgs['tradeDirection'];
  updateOracle: SwapExactInInstructionDataArgs['updateOracle'];
};

export function getSwapExactInInstruction<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountAuthority extends string,
  TAccountVaultIn extends string,
  TAccountVaultOut extends string,
  TAccountToken0Mint extends string,
  TAccountToken1Mint extends string,
  TAccountUserIn extends string,
  TAccountUserOut extends string,
  TAccountTrader extends string,
  TAccountToken0Program extends string,
  TAccountToken1Program extends string,
  TAccountOracle extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: SwapExactInInput<
    TAccountConfig,
    TAccountPool,
    TAccountAuthority,
    TAccountVaultIn,
    TAccountVaultOut,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUserIn,
    TAccountUserOut,
    TAccountTrader,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >,
  config?: { programAddress?: TProgramAddress },
): SwapExactInInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountPool,
  TAccountAuthority,
  TAccountVaultIn,
  TAccountVaultOut,
  TAccountToken0Mint,
  TAccountToken1Mint,
  TAccountUserIn,
  TAccountUserOut,
  TAccountTrader,
  TAccountToken0Program,
  TAccountToken1Program,
  TAccountOracle
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    vaultIn: { value: input.vaultIn ?? null, isWritable: true },
    vaultOut: { value: input.vaultOut ?? null, isWritable: true },
    token0Mint: { value: input.token0Mint ?? null, isWritable: false },
    token1Mint: { value: input.token1Mint ?? null, isWritable: false },
    userIn: { value: input.userIn ?? null, isWritable: true },
    userOut: { value: input.userOut ?? null, isWritable: true },
    trader: { value: input.trader ?? null, isWritable: false },
    token0Program: { value: input.token0Program ?? null, isWritable: false },
    token1Program: { value: input.token1Program ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('authority', accounts.authority),
      getAccountMeta('vaultIn', accounts.vaultIn),
      getAccountMeta('vaultOut', accounts.vaultOut),
      getAccountMeta('token0Mint', accounts.token0Mint),
      getAccountMeta('token1Mint', accounts.token1Mint),
      getAccountMeta('userIn', accounts.userIn),
      getAccountMeta('userOut', accounts.userOut),
      getAccountMeta('trader', accounts.trader),
      getAccountMeta('token0Program', accounts.token0Program),
      getAccountMeta('token1Program', accounts.token1Program),
      getAccountMeta('oracle', accounts.oracle),
    ],
    data: getSwapExactInInstructionDataEncoder().encode(
      args as SwapExactInInstructionDataArgs,
    ),
    programAddress,
  } as SwapExactInInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountAuthority,
    TAccountVaultIn,
    TAccountVaultOut,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUserIn,
    TAccountUserOut,
    TAccountTrader,
    TAccountToken0Program,
    TAccountToken1Program,
    TAccountOracle
  >);
}

export type ParsedSwapExactInInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    pool: TAccountMetas[1];
    authority: TAccountMetas[2];
    vaultIn: TAccountMetas[3];
    vaultOut: TAccountMetas[4];
    token0Mint: TAccountMetas[5];
    token1Mint: TAccountMetas[6];
    userIn: TAccountMetas[7];
    userOut: TAccountMetas[8];
    trader: TAccountMetas[9];
    token0Program: TAccountMetas[10];
    token1Program: TAccountMetas[11];
    oracle?: TAccountMetas[12] | undefined;
  };
  data: SwapExactInInstructionData;
};

export function parseSwapExactInInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSwapExactInInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 13) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 13,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  const getNextOptionalAccount = () => {
    const accountMeta = getNextAccount();
    return accountMeta.address === CPMM_PROGRAM_ADDRESS
      ? undefined
      : accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      pool: getNextAccount(),
      authority: getNextAccount(),
      vaultIn: getNextAccount(),
      vaultOut: getNextAccount(),
      token0Mint: getNextAccount(),
      token1Mint: getNextAccount(),
      userIn: getNextAccount(),
      userOut: getNextAccount(),
      trader: getNextAccount(),
      token0Program: getNextAccount(),
      token1Program: getNextAccount(),
      oracle: getNextOptionalAccount(),
    },
    data: getSwapExactInInstructionDataDecoder().decode(instruction.data),
  };
}
